import { DynamicForm } from "@shared/components/DynamicForm";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Modal } from "@shared/ui/modal";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  CreditCard,
  DollarSign,
  Droplet,
  Layers,
  ReceiptText,
  Send,
  ShieldCheck,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  useApproveBill,
  useApproveDisconnectionBill,
  useBillTemplates,
  useConsumerBill,
  useMeterReadings,
  usePrintBill,
  useRejectBill,
  useSubmitConsumerSelection,
  useUpdateDisconnection,
  useUpdateDisconnectionRequest,
} from "../../../hooks";
import { DisconnectionRequest } from "../../../types";
import MeterRemovalFormWithService, {
  MeterRemovalFormRef,
} from "./RemoveMeterForm";
import SettlementView from "./SettlementView";
import { useNavigate, useParams } from "react-router-dom";

interface SettlementClosureTabProps {
  request: DisconnectionRequest;
  remoteUtilityId: number;
}

const checklistItems = [
  "Final bill generated and sent",
  "Final payment received",
  "Equipment returned/retrieved",
  "Customer notified of closure",
  "Records archived",
  "Account officially closed",
];

const SettlementClosureTab: React.FC<SettlementClosureTabProps> = ({
  request,
  remoteUtilityId,
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const billStatus = request?.additionalData?.billStatus?.toLowerCase();
  // Checklist state
  const [checked, setChecked] = useState<boolean[]>(
    Array(checklistItems.length).fill(false)
  );
  const [billApproved, setBillApproved] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [customerContactData, setCustomerContactData] = useState({
    // name: "",
    // email: "",
    // phone: "",
    name: request?.consumer?.firstName + request?.consumer?.lastName || "N/A",
    email: request?.consumer?.email || "N/A",
    phone: request?.consumer?.contactNumber || "N/A",
  });
  const allChecked = checked.every(Boolean);
  const { data: consumerBill } = useConsumerBill({
    consumerId: request?.consumer.id,
    remoteUtilityId: remoteUtilityId,
  });

  const { data: billTemplates } = useBillTemplates(remoteUtilityId);
  const [showBillSummary, setShowBillSummary] = useState(false);
  const [billResponse, setBillResponse] = useState<any>(null);
  const [billGenerated, setBillGenerated] = useState(false);

  const printBill = usePrintBill();

  const handlePrintClick = async (billId: string) => {
    try {
      printBill.mutateAsync(billId);
    } catch (error) {}
  };
  const { mutateAsync: approveBill, isPending: isApprovingBill } =
    useApproveDisconnectionBill(billResponse?.billId, Number(id));
  const { mutateAsync: rejectBill, isPending: isRejectingBill } = useRejectBill(
    billResponse?.billId
  );

  const handleComplete = async () => {
    setCustomerContactData({
      name: request?.consumer?.firstName + request?.consumer?.lastName || "N/A",
      email: request?.consumer?.email || "N/A",
      phone: request?.consumer?.contactNumber || "N/A",
    });
    setShowApprovalModal(true);
    // const response = await approveBill(); // ✅ Call the mutation
  };

  // const handleFinalApproval = async () => {
  //   try {
  //     if (usingExistingBill) {
  //       setBillApproved(true);
  //     } else {
  //       const response = await approveBill();
  //       setBillApproved(true);
  //     }
  //     setShowApprovalModal(false);
  //   } catch (error) {
  //     console.error("Error approving bill:", error);
  //   }
  // };

  const handleFinalApproval = async () => {
    try {
      const response = await approveBill();
      // setBillApproved(true);
      setShowApprovalModal(false);
    } catch (error) {
      console.error("Error approving bill:", error);
    }
  };

  const handleReject = async () => {
    const response = await rejectBill();
    setShowBillSummary(false);
    setBillGenerated(false);
  };

  const invoiceFields: FormField[] = [
    {
      name: "startDate",
      label: "Start Date",
      type: "datepicker",
      required: true,
      disabled: request?.additionalData?.billId != null,
      placeholder: "Select start date",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "endDate",
      label: "End Date",
      type: "datepicker",
      required: true,
      disabled: request?.additionalData?.billId != null,
      placeholder: "Select end date",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "template",
      label: "Select Template",
      type: "select",
      required: true,
      disabled: request?.additionalData?.billId != null,
      placeholder: "Select Template",
      options:
        billTemplates?.map((item) => ({
          label: item.configName,
          value: item.id,
        })) ?? [],
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  const formConfig = {
    layout: "grid" as const,
    columns: 3,
    spacing: "medium" as const,
    showSubmitButton: false,
    showResetButton: false,
    validateOnMount: false,
    mode: "onChange" as const,
  };

  const formRef = useRef<MeterRemovalFormRef>(null);

  // Modal state
  const [modalStep, setModalStep] = useState(0); // 0: closed, 1: deposit, 2: summary, 3: approve
  const [applyDeposit, setApplyDeposit] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sendToEmail, setSendToEmail] = useState(true);

  // Service Disconnection state
  const [meterAction, setMeterAction] = useState("unassign");
  const form = FormService.createForm({
    fields: invoiceFields,
    ...formConfig,
  });

  // Watch form values
  const formValues = form.watch();

  const { data: meterReadings } = useMeterReadings({
    remoteUtilityId: remoteUtilityId,
    meterId: consumerBill?.result?.meters,
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  });
  const getMeterDisplayData = () => {
    // If we have meter data in additionalData, use it
    if (
      request?.additionalData?.readingData &&
      request.additionalData.readingData.length > 0
    ) {
      return request.additionalData.readingData.map((reading) => ({
        id: reading.meter,
        meterNumber: reading.meterNumber,
        deviceNo: reading.meterNumber, // Same as meter number in this case
        productCode: reading.productCode,
        meterTypeDisplay: "Photo Meter", // You might want to add this to additionalData
        previousReading: reading.meterReading,
        previousReadingDate: reading.readingDate,
        previousReadingMeterStatus: reading.status,
      }));
    }

    // Fallback to meterReadings from hook
    return meterReadings || [];
  };

  const { mutateAsync: updateClosure, isPending: isClosureLoading } =
    useUpdateDisconnectionRequest(request?.id);
  const { mutateAsync: updateDisconnection, isPending: isUpdateLoading } =
    useUpdateDisconnection(request?.id);

  const billData = consumerBill?.result?.billData[0];
  const { mutateAsync: submitConsumer, isPending: isSubmitting } =
    useSubmitConsumerSelection();

  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const [meterReadingsData, setMeterReadingsData] = useState<
    {
      meter: number;
      meter_number: string;
      product_code: string;
      meter_reading: number | null;
      reading_source: number;
      reading_date: string;
      status: number;
    }[]
  >([]);

  console.log("request", request);

  useEffect(() => {
    if (meterReadings && meterReadings.length > 0) {
      const initialReadingsData = meterReadings.map((meter) => ({
        meter: meter.id,
        meter_number: meter.meterNumber,
        product_code: meter.productCode,
        meter_reading: meter.previousReading,
        reading_source: 1, // Default value
        reading_date: meter.previousReadingDate || "",
        status: meter.previousReadingMeterStatus, // Default value
      }));
      setMeterReadingsData(initialReadingsData);
    }
  }, [meterReadings]);

  useEffect(() => {
    // First check if there's a bill in request.additionalData
    if (request?.additionalData?.billId) {
      // Create bill response object from additionalData
      const existingBillResponse = {
        // Core bill identification
        billId: request.additionalData.billId,
        billNumber: request.additionalData.billNumber,
        billDraftId: request.additionalData.billDraftId,

        // Invoice details
        invoicePeriod: request.additionalData.invoicePeriod,
        invoiceStartDate: request.additionalData.invoiceStartDate,
        invoiceEndDate: request.additionalData.invoiceEndDate,

        // Financial amounts
        billAmount: request.additionalData.billAmount || 0,
        totalOutstanding: request.additionalData.balanceBeforeCharges,
        netAmountDue: request.additionalData.netAmountDue,
        securityDeposit: request.additionalData.securityDeposit || 0,
        totalPaymentReceived: request.additionalData.totalPaymentReceived || 0,

        // Dates
        dueDate: request.additionalData.dueDate,
        createdDate: request.additionalData.createdDate,
        lastPayDate: request.additionalData.lastPayDate,

        // Configuration and flags
        billConfigId: request.additionalData.billConfigId,
        billConfigType: request.additionalData.billConfigType,
        billStatus: request.additionalData.billStatus,
        includeCredit: request.additionalData.includeCredit,
        isDepositApplied: request.additionalData.isDepositApplied,
        excludeSecurityDeposit: request.additionalData.excludeSecurityDeposit,
        excludeOutstandingBalance:
          request.additionalData.excludeOutstandingBalance,

        // Request and workflow
        requestId: request.additionalData.requestId,
        additionlaOutstanding: request.additionalData.totalOutstanding,

        preferredTimeSlot: request.additionalData.preferredTimeSlot,

        // Reading data (if available)
        readingData: request.additionalData.readingData || [],

        // Consumer info (from parent request object for consistency)
        consumerId: request?.consumer?.id,
        consumerNo: request?.consumer?.consumerNo,
        firstName: request?.consumer?.firstName,
        lastName: request?.consumer?.lastName,
        contactNumber: request?.consumer?.contactNumber,

        // Meter information (from reading_data if available)
        meterInfo:
          request.additionalData.readingData?.map((reading) => ({
            meter: reading.meter,
            meterNumber: reading.meterNumber,
            productCode: reading.productCode,
            meterReading: reading.meterReading,
            readingDate: reading.readingDate,
            readingSource: reading.readingSource,
            status: reading.status,
          })) || [],
      };

      setBillResponse(existingBillResponse);
      setShowBillSummary(true);
      setBillGenerated(true); // Mark as generated since it exists
      return; // Exit early, don't check consumerBill
    }

    // Original logic for consumerBill (fallback)
    if (
      consumerBill?.result?.billData &&
      consumerBill.result.billData.length > 0
    ) {
      const mostRecentBill = consumerBill.result.billData[0];
      setShowBillSummary(true);
    }
  }, [consumerBill, request?.additionalData]);
  // useEffect(() => {
  //   if (
  //     consumerBill?.result?.billData &&
  //     consumerBill.result.billData.length > 0
  //   ) {
  //     // Use the most recent bill (first one in the array)
  //     const mostRecentBill = consumerBill.result.billData[0];
  //     // setSelectedExistingBill(mostRecentBill);
  //     // setUsingExistingBill(true);
  //     setShowBillSummary(true); // Automatically show the bill summary
  //   }
  // }, [consumerBill]);

  const getBillDisplayData = () => {
    console.log("billResponse in display", billResponse);
    // if (usingExistingBill && selectedExistingBill) {
    //   return {
    //     billNumber: selectedExistingBill.invoiceNo,
    //     invoicePeriod: selectedExistingBill.billPeriod,
    //     billAmount: selectedExistingBill.totalAmountPayable,
    //     totalOutstanding: selectedExistingBill.outstandingBalance,
    //     securityDeposit: selectedExistingBill.securityDeposit || 0,
    //     isDepositApplied: applyDeposit,
    //     billId: selectedExistingBill.id,
    //   };
    // }
    // Return billResponse data for newly generated bills
    return billResponse;
  };
  const getBillSource = () => {
    if (request?.additionalData?.billId) {
      return "additionalData";
    }
    if (billResponse && billGenerated) {
      return "newlyGenerated";
    }

    return "none";
  };

  const handleMeterReadingChange = (
    meterId: number,
    field: "meter_reading" | "reading_date",
    value: string | number
  ) => {
    setMeterReadingsData((prev) =>
      prev.map((meter) =>
        meter.meter === meterId ? { ...meter, [field]: value } : meter
      )
    );
  };

  const getMeterReadingsForSubmission = () => {
    return {
      reading_data: meterReadingsData,
    };
  };

  // Handlers
  const handleGenerateBill = async () => {
    const isValid = await FormService.validateForm(form);
    if (!isValid) {
      return;
    }
    const meterReadingsSubmissionData = getMeterReadingsForSubmission();
    const selectedConfig = billTemplates.find(
      (config) => config.id?.toString() === formValues.template?.toString()
    );
    const payload = {
      requestId: id,
      bill_data: {
        remote_consumer_number: request.consumer.consumerNo,
        remote_utility_id: REMOTE_UTILITY_ID,
        consumer_data: {
          id: request.consumer.id,
          consumer_no: request.consumer.consumerNo,
          status: request.consumer.status,
          is_e_bill: consumerBill?.result?.isEBill,
          is_papered_bill: consumerBill?.result?.isPaperedBill,
        },
        invoice_start_date: formValues.startDate,
        invoice_end_date: formValues.endDate,
        bill_config_id: formValues.template,
        bill_config_type: selectedConfig.source,
        exclude_outstanding_balance: false,
        exclude_security_deposit: false,
        include_credit: true,
      },
      ...meterReadingsSubmissionData,
      bill_generate: "true",
      is_deposit_applied: applyDeposit,
      request_id: request.id,
      step: {
        step_working_on: 2,
        total_steps: 3,
      },
    };
    console.log('payload',payload)

    const response = await submitConsumer(payload);
    if (response) {
      setShowBillSummary(true);
      setBillResponse(response);
      setBillGenerated(true);
    }
  };

  const currency = useCurrency();

  const handleCompleteClosure = async () => {
    const additionalData = {
      is_final_bill_generate_and_send: checked[0], // Final bill generated and sent
      is_payment_received: checked[1], // Final payment received
      is_equipment_received: checked[2], // Equipment returned/retrieved
      is_customer_notified: checked[3], // Customer notified of closure
      is_records_archieved: checked[4], // Records archived
      is_acc_closed: checked[5], // Account officially closed
      is_unassigned_meter: meterAction === "unassign" ? 1 : 0,
    };

    if (meterAction === "unassign") {
      const payload = {
        status: 4,
        is_unassigned_meter: 1,
        additional_data: additionalData,
      };
      await updateDisconnection({
        requestId: request.id,
        payload: payload,
      });
    } else {
      const isValid = await formRef.current?.validateForm();
      if (isValid) {
        const formData = formRef.current?.getFormValues();
        const remote_meter_ids = Object.values(
          formData.selected_device_numbers || {}
        )
          .filter((device: any) => device && device.id)
          .map((device: any) => Number(device.id));

        const payload = {
          remote_workorder_id: formData.selected_work_order.id,
          remote_meter_ids,
          additional_data: additionalData,
        };
        await updateDisconnection({
          requestId: request.id,
          payload: payload,
        });
      }
    }
  };

  const getMeterBackground = (productCode: string) => {
    const code = productCode.toLowerCase();
    if (code.includes("water")) {
      return "bg-cyan-50";
    } else if (code.includes("electric") || code.includes("elctrciity")) {
      return "bg-blue-50";
    }
    return "bg-gray-50";
  };

  const getMeterIcon = (productCode: string) => {
    const code = productCode.toLowerCase();
    if (code.includes("water")) {
      return <Droplet className="h-5 w-5 text-cyan-500" />;
    } else if (code.includes("electric") || code.includes("elctrciity")) {
      return <Zap className="h-5 w-5 text-blue-500" />;
    }
    // Default icon for other types
    return <Zap className="h-5 w-5 text-gray-500" />;
  };

  const utilityServiceList =
    request?.consumer?.consumerMappingData?.map(
      (item) => item.utilityService
    ) || [];

  const [submissionLoading, setSubmissionLoading] = useState(false);

  // Modal content
  const renderModalContent = () => {
    if (modalStep === 2) {
      const displayData = getBillDisplayData();
      return (
        <div className="space-y-4">
          <div className="font-bold text-lg mb-2 text-purple-700">
            Final Bill Summary
          </div>
          <div className="bg-purple-50 rounded p-4 mb-2">
            <div className="flex justify-between mb-2">
              <span>Invoice No:</span>
              <span className="font-semibold">{displayData?.billNumber}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Billing Period:</span>
              <span>{displayData?.invoicePeriod}</span>
            </div>
            <div className="font-semibold mb-2">Bill Details</div>
            <div className="flex justify-between">
              <span>Billed Amount</span>
              <span>{currency}:-{displayData?.billAmount}</span>
            </div>
            <div className="flex justify-between">
              <span>Outstanding Amount</span>
              <span>{currency}:-{displayData?.totalOutstanding}</span>
            </div>
            {applyDeposit && displayData?.securityDeposit > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Security Deposit Applied</span>
                <span>- {currency}:-{displayData?.securityDeposit}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg mt-2">
              <span>Final Amount Due</span>
              <span className="text-red-600">
              {currency}:- {displayData?.additionlaOutstanding}
              </span>
            </div>
          </div>
          <div className="bg-white rounded p-4">
            <div className="font-semibold mb-2">
              Security Deposit Information
            </div>
            <div className="flex justify-between">
              <span>Original Deposit</span>
              <span>{currency}:-{displayData?.securityDeposit}</span>
            </div>
            <div className="flex justify-between">
              <span>Applied to Bill</span>
              <span>{applyDeposit ? "Yes" : "No"}</span>
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button
              className="flex-1 py-2 rounded bg-blue-600 text-white font-semibold"
              onClick={() => setModalStep(1)}
            >
              Back
            </button>
            <button
              className="flex-1 py-2 rounded bg-green-600 text-white font-semibold"
              onClick={() => handleFinalApproval()}
              disabled={isApprovingBill}
            >
              Approve
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const isFinalized =
    request?.statusDisplay === "COMPLETED" ||
    request?.statusDisplay === "REJECTED";

  if (isFinalized) {
    return (
      <SettlementView request={request} remoteUtilityId={remoteUtilityId} />
    );
  }

  return (
    <div className="space-y-6">
      {/* Financial Settlement */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="font-bold text-xl mb-4">Financial Settlement</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Outstanding Balance */}
          <div className="bg-red-50 rounded p-4">
            <div className="font-semibold text-lg text-red-700 flex items-center gap-2 mb-2">
             Outstanding Balance
            </div>
            <div className="flex justify-between">
              <span>Total Outstanding</span>
              <span className="font-bold text-red-600 text-lg">
                {currency}:- {billData?.outstandingBalance ?? "NA"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Due Date</span>
              <span>{billData?.dueDate ?? "NA"}</span>
            </div>
          </div>
          {/* Payment History */}
          <div className="bg-blue-50 rounded p-4">
            <div className="font-semibold text-lg text-blue-700 mb-2">
              Payment History
            </div>
            <div className="flex justify-between">
              <span>Last Payment</span>
              <span>
                {currency}:- {billData?.lastPayAmount ?? "NA"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Payment Date</span>
              <span>{billData?.lastPaymentDate ?? "NA"}</span>
            </div>
          </div>
          {/* Security Deposit */}
          <div className="bg-green-50 rounded p-4">
            <div className="font-semibold text-lg text-green-700 flex items-center gap-2 mb-2">
              <CreditCard className="h-5 w-5" /> Security Deposit
            </div>
            <div className="flex justify-between">
              <span>Deposit Amount</span>
              <span className="font-bold text-green-700">
               {currency}:-{billData?.securityDeposit ?? "NA"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Deposit Date</span>
              <span>{billData?.depositDate ?? "NA"}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={applyDeposit}
                onChange={() => setApplyDeposit((v) => !v)}
              />
              <span>Use in bill</span>
            </div>
          </div>
          {/* Net Settlement */}
          <div className="bg-yellow-50 rounded p-4">
            <div className="font-semibold text-lg text-yellow-700 mb-2">
              Net Settlement
            </div>
            <div className="flex justify-between">
              <span>Outstanding</span>
              <span className="text-red-600">
                {currency}:- {billData?.outstandingBalance || "NA"}
              </span>
            </div>
            {applyDeposit && (
              <div className="flex justify-between">
                <span>Deposit Amount</span>
                <span className="text-red-600">
                  {`${currency}:- ${
                    Number(billData?.securityDeposit || 0)
                  }`}
                </span>
              </div>
            )}

            <div className="flex justify-between font-bold">
              <span>Net Amount</span>
              <span className="text-red-600">
{currency}:-
                {applyDeposit
                  ? Number(billData?.outstandingBalance || 0) -
                    Number(billData?.securityDeposit || 0)
                  : Number(billData?.outstandingBalance || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="font-bold text-xl mb-4 flex items-center gap-2">
          <span className="text-sm">📄</span> Invoice Details
        </div>

        {/* DynamicForm handles its own grid layout */}
        <DynamicForm
          fields={invoiceFields}
          form={form}
          config={formConfig}
          gridColumns={3}
          responsiveColumns={{
            sm: 1,
            md: 2,
            lg: 3,
          }}
          className="invoice-details-form"
          onSubmit={function (data: any): void | Promise<void> {
            throw new Error("Function not implemented.");
          }}
        />
      </div>
      {/* Meter Details */}
      {form.watch("startDate") && form.watch("endDate") && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="font-bold text-xl mb-4 flex items-center gap-2">
            <Layers className="h-5 w-5" /> Meter Details
          </div>
          <div className="flex flex-col gap-4">
            {getMeterDisplayData() && getMeterDisplayData().length > 0 ? (
              getMeterDisplayData().map((meter, index) => (
                <div
                  key={meter.id}
                  className={`rounded p-4 ${getMeterBackground(
                    meter.productCode
                  )}`}
                >
                  <div className="font-semibold text-base flex items-center gap-2 mb-2">
                    {getMeterIcon(meter.productCode)} {meter.productCode} Meter
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500">
                        Meter Number
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1"
                        value={meter.meterNumber}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Device Number
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1"
                        value={meter.deviceNo}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Meter Type
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1"
                        value={meter.meterTypeDisplay}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Last Reading
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1 bg-white"
                        type="number"
                        value={meterReadingsData[index]?.meter_reading || ""}
                        onChange={(e) =>
                          handleMeterReadingChange(
                            meter.id,
                            "meter_reading",
                            e.target.value ? parseFloat(e.target.value) : null
                          )
                        }
                        placeholder="Enter reading"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Reading Date
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1 bg-white"
                        type="date"
                        value={meterReadingsData[index]?.reading_date || ""}
                        onChange={(e) =>
                          handleMeterReadingChange(
                            meter.id,
                            "reading_date",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-center py-4">
                No meter data available
              </div>
            )}
          </div>
        </div>
      )}
      {!request?.additionalData?.billId && !billGenerated && (
        <div className="flex justify-end items-center gap-2">
          <button
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded transition disabled:opacity-60"
            onClick={handleGenerateBill}
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <ReceiptText className="h-5 w-5" />
            Generate Final Bill
          </button>
        </div>
      )}

      {showBillSummary && (billResponse || request?.additionalData?.billId) && (
        <div className="space-y-4">
          {/* Title */}
          <div className="font-bold text-lg text-purple-700">
            {"Final Bill Summary"}
          </div>

          {/* Invoice Info */}
          <div className="bg-white rounded p-4 flex flex-wrap md:flex-nowrap justify-between items-center">
            <div>
              <div className="font-semibold text-base">Invoice Information</div>
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-40 mt-2 md:mt-5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Invoice No:</span>
                  <span className="text-sm font-semibold">
                    {getBillDisplayData()?.billNumber}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Billing Period:</span>
                  <span className="text-sm">
                    {getBillDisplayData()?.invoicePeriod}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bill Details */}
          <div className="bg-white rounded p-4">
            <div className="font-semibold mb-2">Bill Details</div>
            <div className="flex justify-between">
              <span>Billed Amount</span>
              <span>{currency}:-{getBillDisplayData()?.billAmount || "0.00"}</span>
            </div>
            <div className="flex justify-between">
              <span>Outstanding Amount</span>
              <span>{currency}:-{getBillDisplayData()?.totalOutstanding || "0.00"}</span>
            </div>
            {applyDeposit && getBillDisplayData()?.securityDeposit > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Security Deposit Applied</span>
                <span>
                  - {currency}:-{getBillDisplayData()?.securityDeposit || "0.00"}
                </span>
              </div>
            )}
            <hr className="my-2" />
            <div className="flex justify-between font-bold text-lg">
              <span>Final Amount Due</span>
              <span className="text-red-600">
               {currency}:-{getBillDisplayData()?.additionlaOutstanding}
              </span>
            </div>
          </div>

          {/* Security Deposit Info */}
          <div className="bg-white rounded p-4">
            <div className="font-semibold mb-2 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-gray-600" />
              Security Deposit Information
            </div>
            <div className="flex justify-between">
              <span>Deposit Amount</span>
              <span>{currency}:-{getBillDisplayData()?.securityDeposit}</span>
            
            </div>
            <div className="flex justify-between">
              <span>Applied to Bill</span>
              <span>{applyDeposit ? "Yes" : "No"}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-4">
            {/* Print Bill */}
            <button
              className="flex-1 py-2 rounded bg-blue-600 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePrintClick(getBillDisplayData().billId)}
              disabled={printBill.isPending} // disable while loading
            >
              {printBill.isPending ? "Printing..." : "Print Bill"}
            </button>

            {billStatus === "pending" && (
              <button
                className="flex-1 py-2 rounded bg-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-700 transition"
                onClick={() => setShowApprovalModal(true)}
                disabled={isApprovingBill}
              >
                <Send className="h-4 w-4" />
                Approve
              </button>
            )}

            {/* Only show these buttons if bill is NOT approved */}
            {billStatus !== "approved" && !request?.additionalData?.billId && (
              <>
                <button
                  className="flex-1 py-2 rounded border border-gray-300 bg-gray-50 text-black font-semibold hover:bg-gray-100 transition"
                  onClick={() => handleReject()}
                  disabled={isRejectingBill}
                >
                  Discard and Generate New Bill
                </button>
                <button
                  className="flex-1 py-2 rounded bg-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-700 transition"
                  onClick={() => setShowApprovalModal(true)}
                  disabled={isApprovingBill}
                >
                  <Send className="h-4 w-4" />
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {billStatus === "approved" && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="font-bold text-xl mb-4 flex items-center gap-2">
            <XCircle className="h-5 w-5" /> Service Disconnection
          </div>
          <div className="mb-4">
            <div className="font-semibold mb-2">Meter Action Required</div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="radio"
                checked={meterAction === "unassign"}
                onChange={() => setMeterAction("unassign")}
              />
              <span>Unassign Meter Only</span>
            </label>
            <div className="text-gray-500 text-sm mb-2 ml-6">
              Keep the meter in place but unassign it from this account. Meter
              can be reassigned to future customers.
            </div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="radio"
                checked={meterAction === "remove"}
                onChange={() => setMeterAction("remove")}
              />
              <span>Remove Meter Completely</span>
            </label>
            <div className="text-gray-500 text-sm mb-2 ml-6">
              Physically remove the meter from the premises. This option is
              typically used when the meter is damaged or the service location
              is being permanently discontinued.
            </div>
            {meterAction === "remove" && (
              <MeterRemovalFormWithService
                ref={formRef}
                utilityServiceList={utilityServiceList}
                loading={submissionLoading}
                // onFormDataChange={(data) => setMeterRemovalFormData(data)}
                // onValidationChange={(isValid) => setIsMeterFormValid(isValid)}
              />
            )}
          </div>
        </div>
      )}

      {/* Account Closure Checklist */}
      {billStatus === "approved" && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="font-bold text-xl mb-4">
            Account Closure Checklist
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {checklistItems.map((item, idx) => (
              <label
                key={item}
                className="flex items-center gap-2 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  className="form-checkbox h-5 w-5 text-blue-600"
                  checked={checked[idx]}
                  onChange={() =>
                    setChecked((prev) =>
                      prev.map((v, i) => (i === idx ? !v : v))
                    )
                  }
                />
                <span className="text-base">{item}</span>
              </label>
            ))}
          </div>

          <div className="text-gray-500 text-sm mb-4">
            Completion: {checked.filter(Boolean).length}/{checklistItems.length}{" "}
            items
          </div>

          <button
            className="w-full md:w-auto px-6 py-2 bg-red-400 hover:bg-red-500 text-white font-semibold rounded disabled:opacity-60 justify-end"
            onClick={handleCompleteClosure}
            disabled={!allChecked || isClosureLoading}
          >
            Execute Disconnection
          </button>
        </div>
      )}

      {/* Service Disconnection */}

      {/* Modal for Bill Generation Flow */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalStep(0);
        }}
        title={
          modalStep === 1
            ? "Security Deposit Options"
            : modalStep === 2
            ? "Final Bill Summary"
            : modalStep === 3
            ? "Approve and Send Final Bill"
            : ""
        }
      >
        {renderModalContent()}
      </Modal>
      <Modal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        title="Approve and Send Final Bill"
      >
        <div className="space-y-4">
          {/* Customer Contact Details */}
          <div className="bg-blue-50 rounded p-4">
            <div className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
              <span>👤</span> Customer Contact Details
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Name:</span>
                <span className="text-sm">{customerContactData.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Email:</span>
                <span className="text-sm">{customerContactData.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Phone:</span>
                <span className="text-sm">{customerContactData.phone}</span>
              </div>
            </div>
          </div>

          {/* Bill Summary */}

          <div className="bg-green-50 rounded p-4">
            <div className="font-semibold mb-2">Bill Summary</div>

            <div className="flex justify-between">
              <span>Billed Amount</span>
              <span>{currency}:-{billData?.totalAmountPayable ?? 0}</span>
            </div>

            <div className="flex justify-between">
              <span>Outstanding Amount</span>
              <span>{currency}:-{billData?.outstandingBalance ?? 0}</span>
            </div>

            {applyDeposit && (
              <div className="flex justify-between text-green-700">
                <span>Security Deposit Applied</span>
                <span>-{currency}:-{billData?.securityDeposit ?? 0}</span>
              </div>
            )}

            <div className="flex justify-between font-bold text-lg mt-2">
              <span>Final Amount Due</span>
              <span className="text-red-600">
               {currency}:-
                {(() => {
                  const billed = Number(billData?.totalAmountPayable) || 0;
                  const outstanding = Number(billData?.outstandingBalance) || 0;
                  const deposit = Number(billData?.securityDeposit) || 0;

                  const finalAmount =
                    billed + outstanding - (applyDeposit ? deposit : 0);
                  return finalAmount.toFixed(2);
                })()}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-4">
            <button
              className="flex-1 py-2 rounded border border-gray-300 bg-gray-50 text-black font-semibold hover:bg-gray-100 transition"
              onClick={() => setShowApprovalModal(false)}
            >
              Cancel
            </button>
            <button
              className="flex-1 py-2 rounded bg-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-700 transition"
              onClick={handleFinalApproval}
              disabled={isApprovingBill}
            >
              {isApprovingBill && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <Send className="h-4 w-4" />
              Approve
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SettlementClosureTab;
